import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { Product } from '../../types';

interface ReviewProps {
  products: Product[];
}

const Review: React.FC<ReviewProps> = ({ products }) => {
  const { reviews, products: adminProducts } = useAdmin();
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 12;

  const maskEmail = (email: string) => {
    if (!email || !email.includes('@')) return email;
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 3) return `${localPart.charAt(0)}***@${domain}`;
    return `${localPart.slice(0, 3)}***@${domain}`;
  };

  const approvedReviews = reviews.filter(review => (review.status || (review.is_verified ? "approved" : "pending")) === "approved");

  const filteredReviews = useMemo(() => {
    return [...approvedReviews].sort((a, b) => {
      if (activeFilter === 'Top Rated') return b.rating - a.rating;
      return new Date(b.createdAt || b.created_at || new Date()).getTime() - new Date(a.createdAt || a.created_at || new Date()).getTime();
    });
  }, [approvedReviews, activeFilter]);

  useEffect(() => { window.scrollTo(0, 0); }, [currentPage, activeFilter]);

  const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage);
  const startIndex = (currentPage - 1) * reviewsPerPage;
  const currentReviews = filteredReviews.slice(startIndex, startIndex + reviewsPerPage);

  const avgRating = approvedReviews.length > 0
    ? (approvedReviews.reduce((sum, r) => sum + r.rating, 0) / approvedReviews.length).toFixed(1)
    : '0.0';

  const satisfaction = approvedReviews.length > 0
    ? Math.round((approvedReviews.filter(r => r.rating >= 4).length / approvedReviews.length) * 100)
    : 0;

  // Rating distribution
  const ratingDist = useMemo(() => {
    const dist = [0, 0, 0, 0, 0];
    approvedReviews.forEach(r => { if (r.rating >= 1 && r.rating <= 5) dist[r.rating - 1]++; });
    const max = Math.max(...dist, 1);
    return dist.map((count, i) => ({ stars: i + 1, count, pct: Math.round((count / max) * 100) }));
  }, [approvedReviews]);

  return (
    <div className="min-h-screen bg-cream-50">

      {/* ===== Hero Stats — Editorial Full-width ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-forest-50/80 via-cream-50 to-forest-100/40 pointer-events-none" />
        <div className="relative section-container py-16 sm:py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

            {/* Left — Big Rating */}
            <div>
              <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-6 block">Customer Reviews</span>
              <div className="flex items-baseline gap-4 mb-4">
                <span className="font-display text-[7rem] sm:text-[9rem] leading-none text-forest-900 tracking-tighter">{avgRating}</span>
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className={`w-6 h-6 ${i < Math.round(Number(avgRating)) ? 'text-gold-400' : 'text-warm-200'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-body-sm text-warm-500">{approvedReviews.length} verified reviews</p>
                </div>
              </div>

              <div className="flex gap-8 mt-8">
                <div>
                  <div className="font-display text-display-sm text-forest-900">{satisfaction}%</div>
                  <p className="text-micro text-warm-500 mt-1">Satisfaction</p>
                </div>
                <div className="w-px bg-warm-200" />
                <div>
                  <div className="font-display text-display-sm text-forest-900">{approvedReviews.filter(r => r.images && r.images.length > 0).length}</div>
                  <p className="text-micro text-warm-500 mt-1">Photo reviews</p>
                </div>
              </div>
            </div>

            {/* Right — Rating Distribution + CTA */}
            <div>
              <div className="bg-cream-50/70 backdrop-blur-xl border border-warm-200/60 rounded-2xl p-8 shadow-card">
                <h3 className="text-caption font-semibold text-forest-900 mb-6 uppercase tracking-wider">Rating Breakdown</h3>
                <div className="space-y-3">
                  {ratingDist.reverse().map((d) => (
                    <div key={d.stars} className="flex items-center gap-3">
                      <span className="text-caption font-medium text-warm-500 w-12">{d.stars} star</span>
                      <div className="flex-1 h-2.5 bg-warm-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gold-400 rounded-full transition-all duration-700 ease-spring"
                          style={{ width: `${d.pct}%` }}
                        />
                      </div>
                      <span className="text-micro text-warm-400 w-8 text-right">{d.count}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-warm-200">
                  <Link
                    to="/review/write"
                    className="btn-primary w-full justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Write a Review
                  </Link>
                  <p className="text-center text-micro text-warm-400 mt-3">
                    Photo <span className="font-semibold text-warm-600">$10</span> · Text <span className="font-semibold text-warm-600">$5</span> credit
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Filter + Grid ===== */}
      <section className="border-t border-warm-200">
        <div className="section-container">

          {/* Sticky filter bar */}
          <div className="sticky top-[72px] z-10 bg-cream-50/80 backdrop-blur-2xl py-5 border-b border-warm-200 -mx-6 px-6 sm:-mx-8 sm:px-8 lg:-mx-16 lg:px-16">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {[
                  { key: 'All', label: 'All' },
                  { key: 'Latest', label: 'Latest' },
                  { key: 'Top Rated', label: 'Top Rated' },
                ].map((option) => (
                  <button
                    key={option.key}
                    onClick={() => { setActiveFilter(option.key); setCurrentPage(1); }}
                    className={`px-5 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                      activeFilter === option.key
                        ? 'bg-forest-800 text-cream-100 border border-forest-700/50'
                        : 'bg-warm-100 text-warm-600 hover:bg-warm-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <span className="text-caption text-warm-400">{filteredReviews.length} reviews</span>
            </div>
          </div>

          {/* Review Cards — Masonry-ish 3-column */}
          <div className="py-10 lg:py-14">
            {currentReviews.length === 0 ? (
              <div className="text-center py-24">
                <div className="w-16 h-16 bg-warm-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-warm-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="font-display text-heading text-forest-900 mb-2">No reviews yet</h3>
                <p className="text-body-sm text-warm-500 mb-6">Be the first to share your experience.</p>
                <Link to="/review/write" className="btn-accent text-sm px-6 py-3">Write a Review</Link>
              </div>
            ) : (
              <div className="columns-1 md:columns-2 xl:columns-3 gap-5 space-y-5">
                {currentReviews.map((review) => {
                  const product = adminProducts.find(p => p.id === review.productId);
                  const hasImage = review.images && review.images.length > 0;
                  return (
                    <div key={review.id} className="break-inside-avoid bg-cream-50 border border-warm-200 rounded-xl overflow-hidden hover:shadow-hover hover:-translate-y-0.5 transition-all duration-500 ease-spring">
                      {/* Review image */}
                      {hasImage && (
                        <img
                          src={review.images![0]}
                          alt="Review"
                          className="w-full h-56 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(review.images![0], '_blank')}
                        />
                      )}

                      <div className="p-6">
                        {/* Stars */}
                        <div className="flex items-center gap-2 mb-4">
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <svg key={i} className={`w-4 h-4 ${i < Math.floor(review.rating) ? 'text-gold-400' : 'text-warm-200'}`} fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <span className="text-caption font-semibold text-forest-900">{review.rating.toFixed(1)}</span>
                        </div>

                        {/* Content */}
                        <p className="text-body-sm text-forest-700 leading-relaxed mb-5">{review.content}</p>

                        {/* Meta */}
                        <div className="flex items-center justify-between pt-4 border-t border-warm-200">
                          <div>
                            <span className="text-caption font-medium text-forest-700 block">{maskEmail(review.userEmail || review.userName || 'Anonymous')}</span>
                            <span className="text-micro text-warm-400">
                              {new Date(review.createdAt || review.created_at || new Date()).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </div>

                        {/* Product link */}
                        <Link
                          to={`/product/${review.productId}`}
                          className="flex items-center gap-3 bg-cream-100 rounded-xl p-3 mt-4 hover:bg-warm-100 transition-colors group"
                        >
                          <div className="w-9 h-9 bg-cream-50 rounded-lg border border-warm-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {product?.image ? (
                              <img src={product.image} alt="" className="w-full h-full object-contain" />
                            ) : (
                              <svg className="w-4 h-4 text-warm-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            )}
                          </div>
                          <span className="text-caption text-warm-600 font-medium line-clamp-1 flex-1">{product?.name || review.productName || 'Product'}</span>
                          <svg className="w-3.5 h-3.5 text-warm-400 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-14">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-5 py-2.5 rounded-lg border border-warm-200 text-caption font-medium text-warm-600 bg-cream-50 hover:bg-cream-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  Prev
                </button>
                {(() => {
                  const delta = 2;
                  const range: (number | string)[] = [];
                  const start = Math.max(1, currentPage - delta);
                  const end = Math.min(totalPages, currentPage + delta);
                  if (start > 1) { range.push(1); if (start > 2) range.push('...'); }
                  for (let i = start; i <= end; i++) range.push(i);
                  if (end < totalPages) { if (end < totalPages - 1) range.push('...'); range.push(totalPages); }
                  return range.map((page, index) =>
                    page === '...' ? (
                      <span key={`dots-${index}`} className="px-1.5 text-warm-400">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page as number)}
                        className={`w-10 h-10 rounded-lg text-caption font-medium transition-all ${
                          currentPage === page ? 'bg-gold-400 text-forest-900' : 'border border-warm-200 text-warm-600 bg-cream-50 hover:bg-cream-100'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  );
                })()}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-5 py-2.5 rounded-lg border border-warm-200 text-caption font-medium text-warm-600 bg-cream-50 hover:bg-cream-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Review;
